// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import { Flex, Typography, useToast } from '@iheartjane/reefer';
import { Form } from '@iheartjane/reefer-hook-form';
import { useMaskito } from '@maskito/react';
import noop from 'lodash-es/noop';
import { useCallback, useEffect } from 'react';
import type { FieldValues, SubmitHandler } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';

import {
  useFetchCurrentBloomMenu,
  useUpdateBloomMenu,
} from '@jane/business-admin/data-access';
import { alwaysStartsWith, businessPaths } from '@jane/business-admin/util';

import { ModalPageWrapper } from './components/ModalPageWrapper';

import styles from './PremiumGoogleAddons.module.css';

const StyledLink = styled('a')({
  textDecoration: 'underline',
});

export const PremiumGoogleAddons = () => {
  const gtmRef = useMaskito({
    options: {
      mask: /^GTM-.*$/,
      preprocessors: [alwaysStartsWith('GTM-')],
    },
  });
  const navigate = useNavigate();
  const toast = useToast();
  const bloomMenuQuery = useFetchCurrentBloomMenu();

  const formMethods = useForm({
    defaultValues: {
      google_sso_client_id: bloomMenuQuery.data?.google_sso_client_id ?? '',
      google_sso_client_secret:
        bloomMenuQuery.data?.google_sso_client_secret ?? '',
      google_tag_manager_container_id:
        bloomMenuQuery.data?.google_tag_manager_container_id ?? '',
    },
  });
  const { control, reset } = formMethods;

  const { mutate: updateBloomMenu, isPending } = useUpdateBloomMenu(
    bloomMenuQuery.data?.id
  );

  const publish: SubmitHandler<FieldValues> = useCallback(
    (data) => {
      updateBloomMenu(
        {
          google_sso_client_id: data['google_sso_client_id'],
          google_sso_client_secret: data['google_sso_client_secret'],
          google_tag_manager_container_id:
            data['google_tag_manager_container_id'],
        },
        {
          onError: () => {
            toast.add({
              label: 'An error occurred.',
              variant: 'error',
            });
          },
          onSuccess: () => {
            toast.add({
              label: 'Menu updated!',
              variant: 'success',
            });
            reset({
              google_sso_client_id: data['google_sso_client_id'],
              google_sso_client_secret: data['google_sso_client_secret'],
              google_tag_manager_container_id:
                data['google_tag_manager_container_id'],
            });
            navigate(businessPaths.premium());
          },
        }
      );
    },
    [updateBloomMenu, toast, reset, navigate]
  );

  useEffect(() => {
    if (bloomMenuQuery.isSuccess) {
      reset({
        google_sso_client_id: bloomMenuQuery.data?.google_sso_client_id ?? '',
        google_sso_client_secret:
          bloomMenuQuery.data?.google_sso_client_secret ?? '',
        google_tag_manager_container_id:
          bloomMenuQuery.data?.google_tag_manager_container_id ?? '',
      });
    }
  }, [bloomMenuQuery.data, bloomMenuQuery.isSuccess, reset]);

  return (
    <ModalPageWrapper
      formMethods={formMethods}
      isLoading={isPending}
      onSubmit={publish}
      title="Google add-ons"
    >
      <Form name="google_sso_form" onSubmit={noop}>
        <Flex width="100%" justifyContent="center">
          <Flex
            className={styles['formFields']}
            borderRadius="lg"
            flexDirection="column"
            maxWidth={864}
            mt={40}
          >
            <Flex
              className={styles['formSection']}
              flexDirection="column"
              p={40}
              gap={24}
            >
              <Typography branded variant="header-bold">
                Google SSO
              </Typography>
              <Typography>
                You can set up single sign-on (SSO) with Google so that your
                customers can sign in with Google. Allowing your customers to
                sign in with Google will reduce friction in your customers&apos;
                login experience.
              </Typography>
              <Typography>
                Follow the steps in this{' '}
                <StyledLink href="https://docs.iheartjane.com/partner-success/men-you/jane-bloom">
                  guide
                </StyledLink>{' '}
                to set up Google SSO for your menu.
              </Typography>
              <Flex gap={24}>
                <Controller
                  name="google_sso_client_id"
                  control={control}
                  render={({ field }) => (
                    <Form.TextField
                      {...field}
                      label="Google SSO Client ID"
                      placeholder="1234567890-abc123def456.apps.googleusercontent.com"
                    />
                  )}
                />
                <Controller
                  name="google_sso_client_secret"
                  control={control}
                  render={({ field }) => (
                    <Form.TextField
                      {...field}
                      label="Google SSO Client Secret"
                    />
                  )}
                />
              </Flex>
            </Flex>

            <Flex
              className={styles['formSection']}
              flexDirection="column"
              p={40}
              gap={24}
            >
              <Typography branded variant="header-bold">
                Google Tag Manager
              </Typography>
              <Typography>
                Directly integrate Google Tag Manager into your menu. Please
                note that as a site owner, you are responsible for making sure
                your site is handling data in a way that is in line with privacy
                standards such as GDPR.
              </Typography>
              <Controller
                name="google_tag_manager_container_id"
                control={control}
                render={({ field }) => (
                  <Form.TextField
                    {...field}
                    label="GTM Container ID"
                    placeholder="GTM-XXXXXX"
                    ref={gtmRef}
                  />
                )}
              />
            </Flex>
          </Flex>
        </Flex>
      </Form>
    </ModalPageWrapper>
  );
};
